import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import {
    CHEMICALS_API,
    CHEMICALS_ENRICH_API,
    CHEMICAL_DB_API_HOST,
    JSON_HEADERS
} from './constants';
import type { EnrichChemical, EnrichedChemical, FetchChemical } from './types';

const useChemical = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost } = useFetch({
        apiHost: CHEMICAL_DB_API_HOST,
        headers: JSON_HEADERS
    });
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, ENRICH_FAILED } = useTranslation();

    const fetchChemical: FetchChemical = useCallback(
        async ({ id }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () => authenticatedGet<EnrichedChemical>(`${CHEMICALS_API}/${id}`),
                    CONNECTION_ERROR,
                    () => fetchChemical({ id })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedGet, withRetry, CONNECTION_ERROR]
    );

    const enrichChemical: EnrichChemical = useCallback(
        async ({ agrianProductId }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () =>
                        authenticatedPost<EnrichedChemical>(
                            CHEMICALS_ENRICH_API,
                            JSON.stringify({ agrianProductId })
                        ),
                    ENRICH_FAILED,
                    () => enrichChemical({ agrianProductId })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedPost, withRetry, ENRICH_FAILED]
    );

    return {
        enrichChemical,
        fetchChemical,
        isLoading
    };
};

export default useChemical;
